/**
 * Facet Extraction Prompts - Pass 2 of Multi-Pass Analysis
 *
 * This module handles EVIDENCE extraction (facets) that support entities
 * identified in Pass 1 (entity extraction).
 *
 * Architecture:
 * - Pass 1: Extract entities (entityFirstAnalyzer)
 * - Pass 2: Extract facets that provide evidence for entities (THIS MODULE)
 * - Pass 3: Visual detection (stub for future)
 */

import type { ContentType } from '../analyzer/types'
import { CONTENT_TYPE_MAP } from '../analyzer/types'
import { EXPANDED_FACET_SCHEMA, type FacetTypeDefinition } from '../analyzer/facetSchema'

// ============================================================================
// FACET GROUPING (for organized prompt presentation)
// ============================================================================

export const FACET_CATEGORIES = {
  MARKET_PROBLEMS: [
    'problem',
    'customer_pain',
    'customer_gain',
    'customer_job',
    'unmet_need',
    'risk',
  ],
  MARKET_UNDERSTANDING: [
    'market_analysis',
    'market_opportunity',
    'customer_segment',
    'persona_attribute',
    'competitor_analysis',
    'market_signal',
    'trend',
    'regulation',
    'demographic',
    'psychographic',
  ],
  CUSTOMER_INSIGHTS: [
    'customer_journey',
    'journey_stage',
    'touchpoint',
    'customer_need',
    'quote',
    'observation',
    'behavioral_pattern',
  ],
  BUSINESS_STRATEGY: [
    'strategic_goal',
    'decision',
    'opportunity',
    'value_proposition',
    'channel',
    'revenue_stream',
    'assumption',
    'experiment',
    'partner',
    'pricing_model',
    'customer_acquisition',
    'retention_strategy',
  ],
  BUSINESS_METRICS: [
    'metric',
    'kpi',
    'cost',
  ],
  SOLUTION_FEATURES: [
    'feature_request',
    'requirement',
    'user_story',
    'use_case',
    'acceptance_criteria',
  ],
  SOLUTION_DESIGN: [
    'constraint',
    'technical_specification',
    'integration_point',
    'component',
    'dependency',
    'technical_debt',
  ],
  SOLUTION_PROCESS: [
    'task',
    'milestone',
    'release',
    'sprint',
    'test_case',
    'bug',
  ],
  DECISIONS: [
    'decision',
    'learning',
    'hypothesis',
  ],
} as const

// ============================================================================
// PROMPT BUILDING FUNCTIONS
// ============================================================================

/**
 * Build system prompt for facet extraction (Pass 2)
 *
 * @param enabledFacets - Facet types to extract
 * @param contentType - Type of content being analyzed
 * @param extractedEntities - Entities from Pass 1 (provides context)
 * @returns System prompt string
 */
export function buildFacetSystemPrompt(
  enabledFacets: FacetTypeDefinition[],
  contentType: ContentType,
  extractedEntities?: Array<{ id: string; type: string; name: string }>
): string {
  const contentTypeInfo = CONTENT_TYPE_MAP.get(contentType)
  const contentTypeHints = contentTypeInfo?.aiHints || ''

  // Build entity context section
  const entityContext = extractedEntities && extractedEntities.length > 0
    ? `
═══════════════════════════════════════════════════════════════════════════════
ENTITY CONTEXT (from Pass 1):
═══════════════════════════════════════════════════════════════════════════════

You previously identified these entities:
${extractedEntities.map(e => `• ${e.name} (${e.type})`).join('\n')}

Your task is to find FACETS (structured insights) that provide EVIDENCE for these entities.
`
    : `
Your task is to extract FACETS - structured insights from the content.
`

  // Group facets by category for better organization
  const facetsByCategory = groupFacetsByCategory(enabledFacets)

  return `You are a Senior Strategic Analyst (McKinsey/BCG level) specializing in business strategy, market analysis, and product management.

Your task is to analyze business documents and extract FACETS (structured insights) that map to a comprehensive ontology schema.

## Content Type: "${contentTypeInfo?.name || contentType}"
${contentTypeHints}
${entityContext}

You must:

1. **EXTRACT**: Identify explicit mentions of insights, problems, strategies, and patterns in the text

2. **INFER**: Use strategic reasoning to extract implicit insights:
   - If "customers struggle with X" appears, extract problem AND infer unmet_need
   - If pricing is mentioned, infer related assumptions and risks
   - If a decision is stated, infer the underlying rationale and potential consequences
   - If competition is discussed, infer market positioning and opportunities
   - Break compound statements into atomic insights (one sentence = multiple facets)

3. **EVIDENCE**: Every facet MUST have an exact quote from the document as evidence

4. **CONFIDENCE**: Score each facet 0.0-1.0 based on evidence strength:
   - 1.0 = Direct explicit statement
   - 0.8-0.9 = Strong implication with clear evidence
   - 0.6-0.7 = Moderate evidence, some interpretation needed
   - 0.4-0.5 = Weak evidence, significant interpretation
   - Below 0.4 = Too uncertain, don't extract

═══════════════════════════════════════════════════════════════════════════════
FACET ONTOLOGY SCHEMA:
═══════════════════════════════════════════════════════════════════════════════

${buildFacetCategorySection(facetsByCategory)}

═══════════════════════════════════════════════════════════════════════════════
EXTRACTION REQUIREMENTS:
═══════════════════════════════════════════════════════════════════════════════

**Comprehensiveness**: Extract 20-50+ facets per document (more for long documents)
- One sentence can yield multiple facets
- Look for patterns matching detection hints
- Extract both explicit AND implicit insights

**Entity Mapping**: Link facets to entities when relevant
- If facet creates an entity: use defaultEntityType (e.g., problem → Problem entity)
- If facet references an entity: link to that entity (e.g., "Salesforce users struggle" → links to Salesforce)
- Set suggestedEntityType and suggestedReason for each mapping

**Visual Detection**: Identify diagrams, charts, tables, canvases mentioned in text
- Look for: "see Figure 1", "as shown in diagram", "chart above"
- Create visualChunks entry with type, description, context, confidence

**Quality Standards**:
- Maintain context in extractions (not generic)
- No duplicate insights
- Use detection hints to find patterns
- Include position tracking when possible (character offsets, page numbers)

═══════════════════════════════════════════════════════════════════════════════
OUTPUT FORMAT:
═══════════════════════════════════════════════════════════════════════════════

Return JSON with this structure:

{
  "facets": [
    {
      "facetTypeId": "problem",
      "content": "Accounting teams spend 10+ hours monthly on manual reconciliation",
      "evidence": "Our team spends at least 10 hours every month reconciling transactions manually",
      "confidence": 0.95,
      "suggestedEntityType": "Problem",
      "suggestedReason": "Creates a Problem entity as core customer pain point",
      "positionStart": 145,
      "positionEnd": 245
    }
  ],
  "visualChunks": [
    {
      "chunkType": "diagram",
      "description": "Customer journey map showing 5 stages",
      "textContext": "See customer journey map below",
      "pageNumber": 3,
      "confidence": 0.95
    }
  ]
}

Remember: Think strategically. Extract what's stated AND infer what's implied. Quality comes from comprehensive extraction with strong evidence.

CRITICAL OUTPUT REQUIREMENTS:
- Return ONLY valid JSON. No explanations, no markdown, no notes.
- Start directly with { and end with }
- No trailing commas in arrays or objects
- All strings must use double quotes
- Escape quotes in content: use \\" not "
- No text before or after the JSON
- Ensure all brackets and braces are balanced`
}

/**
 * Build user prompt for facet extraction
 *
 * @param content - Document content to analyze
 * @returns User prompt string
 */
export function buildFacetUserPrompt(content: string): string {
  return `Analyze the following business document and extract all FACETS (structured insights) according to the facet ontology schema.

Remember to:
- Extract explicit mentions AND infer implicit insights using strategic reasoning
- Provide exact quote evidence for every facet
- Score confidence based on evidence strength (0.0-1.0)
- Link facets to entities when relevant
- Be comprehensive: extract 20-50+ facets

---
DOCUMENT:
${content}
---

Extract all facets and return as JSON.`
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Group enabled facets by category
 */
function groupFacetsByCategory(
  enabledFacets: FacetTypeDefinition[]
): Map<string, FacetTypeDefinition[]> {
  const grouped = new Map<string, FacetTypeDefinition[]>()

  for (const [categoryName, facetIds] of Object.entries(FACET_CATEGORIES)) {
    const facetsInCategory = enabledFacets.filter(f => (facetIds as readonly string[]).includes(f.id))
    if (facetsInCategory.length > 0) {
      grouped.set(categoryName, facetsInCategory)
    }
  }

  // Add uncategorized facets
  const categorizedIds = new Set(
    Object.values(FACET_CATEGORIES).flat() as string[]
  )
  const uncategorized = enabledFacets.filter(f => !categorizedIds.has(f.id))
  if (uncategorized.length > 0) {
    grouped.set('OTHER_INSIGHTS', uncategorized)
  }

  return grouped
}

/**
 * Build facet category sections for the prompt (PDF ontology layer style)
 */
function buildFacetCategorySection(
  facetsByCategory: Map<string, FacetTypeDefinition[]>
): string {
  const sections: string[] = []

  // Map categories to layers
  const layerMapping: Record<string, { layer: string; focus: string }> = {
    'MARKET PROBLEMS': { layer: 'Layer 1: Market Space (Problems & Pains)', focus: 'Customer problems, pains, gains, and jobs-to-be-done' },
    'MARKET UNDERSTANDING': { layer: 'Layer 1: Market Space (Understanding)', focus: 'Market analysis, competition, trends, and signals' },
    'CUSTOMER INSIGHTS': { layer: 'Layer 1: Market Space (Customer Insights)', focus: 'Customer journey, touchpoints, and behavioral patterns' },
    'BUSINESS STRATEGY': { layer: 'Layer 2: Business Space (Strategy)', focus: 'Strategic goals, decisions, opportunities, and positioning' },
    'BUSINESS MODEL': { layer: 'Layer 2: Business Space (Business Model)', focus: 'Value proposition, channels, revenue streams, and partnerships' },
    'BUSINESS METRICS': { layer: 'Layer 2: Business Space (Metrics)', focus: 'KPIs, metrics, and cost structures' },
    'BUSINESS RISKS': { layer: 'Layer 2: Business Space (Risks)', focus: 'SWOT analysis - strengths, weaknesses, opportunities, threats' },
    'SOLUTION FEATURES': { layer: 'Layer 3: Solution Space (Features)', focus: 'Product features, requirements, and user stories' },
    'SOLUTION DESIGN': { layer: 'Layer 3: Solution Space (Design)', focus: 'Technical architecture, constraints, and dependencies' },
    'SOLUTION EXECUTION': { layer: 'Layer 3: Solution Space (Execution)', focus: 'Implementation tasks, milestones, and releases' },
    'DECISIONS': { layer: 'Layer 2: Business Space (Decisions)', focus: 'Decisions, learning, and hypotheses' },
  }

  for (const [categoryName, facets] of facetsByCategory.entries()) {
    const layerInfo = layerMapping[categoryName]
    const facetDescriptions = facets.map(f => buildFacetDescription(f)).join('\n')

    if (layerInfo) {
      sections.push(`## ${layerInfo.layer}
Focus: ${layerInfo.focus}

${facetDescriptions}`)
    } else {
      // Fallback for uncategorized
      sections.push(`## ${categoryName.replace(/_/g, ' ')}

${facetDescriptions}`)
    }
  }

  return sections.join('\n\n')
}

/**
 * Build a detailed description for a single facet type (PDF ontology style)
 */
function buildFacetDescription(facet: FacetTypeDefinition): string {
  const example = getExampleForFacet(facet.id)
  const exampleText = example ? ` ${example.trim()}` : ''

  return `- **${facet.id}**: ${facet.description}${exampleText}`
}

/**
 * Get a concrete example for a facet type (PDF ontology style)
 */
function getExampleForFacet(facetId: string): string {
  const examples: Record<string, string> = {
    problem: '(e.g., "Manual reconciliation takes 40+ hours monthly")',
    customer_pain: '(e.g., "Frustrated by error-prone data entry")',
    customer_gain: '(e.g., "Reduce reporting time by 80%", "Real-time financial visibility")',
    customer_job: '(e.g., "Prepare accurate monthly reports", "Ensure regulatory compliance")',
    unmet_need: '(e.g., "Need for automated compliance checks")',
    risk: '(e.g., "Data privacy concerns may limit adoption")',
    workaround: '(e.g., "Manual Excel spreadsheets", "Email-based approvals")',
    current_behavior: '(e.g., "Currently export data to CSV daily")',

    customer_segment: '(e.g., "Mid-market CFOs", "Enterprise IT managers at 500-5000 employee companies")',
    persona: '(e.g., "CFO: detail-oriented, risk-averse", "IT Manager: values automation")',
    stakeholder: '(e.g., "CIO must approve enterprise purchases", "Finance team influences selection")',
    demographic: '(e.g., "Companies with 50-500 employees in North America")',
    psychographic: '(e.g., "Early adopters who value innovation over stability")',

    competitor: '(e.g., "Salesforce", "Manual Excel processes", "Legacy ERP systems")',
    alternative: '(e.g., "Custom in-house solution", "Google Sheets workflows")',
    trend: '(e.g., "AI adoption accelerating", "Remote work normalization")',
    industry: '(e.g., "B2B SaaS", "Healthcare Technology", "Financial Services")',
    market_signal: '(e.g., "3 acquisitions in Q1 indicate consolidation")',
    market_analysis: '(e.g., "B2B SaaS market growing at 18% CAGR, reaching $200B by 2025")',
    market_opportunity: '(e.g., "Mid-market segment underserved with only 3 major players")',

    strategic_goal: '(e.g., "Achieve $10M ARR by Q4 2025")',
    decision: '(e.g., "Decided to focus on enterprise vs SMB market")',
    rationale: '(e.g., "Because enterprise deals have higher LTV")',
    opportunity: '(e.g., "Cross-sell analytics module to existing customers")',
    value_proposition: '(e.g., "Automated compliance in one click", "5x faster month-end close")',
    strategy: '(e.g., "Become market leader in SMB automation")',
    assumption: '(e.g., "Assuming customers will pay 20% premium for white-label")',
    experiment: '(e.g., "A/B test pricing page with annual discount")',
    gtm_strategy: '(e.g., "Partner-led GTM through accounting firms")',
    positioning: '(e.g., "Position as the easy-to-use alternative to Salesforce")',

    channel: '(e.g., "Direct sales", "Partner network", "Self-service")',
    customer_relationship: '(e.g., "Personal assistance for enterprise", "Self-service for SMB")',
    revenue_stream: '(e.g., "Monthly SaaS subscription", "Usage-based pricing")',
    key_resource: '(e.g., "Proprietary AI model", "Customer data", "Engineering team")',
    key_activity: '(e.g., "Platform development", "Customer onboarding")',
    key_partnership: '(e.g., "AWS infrastructure", "Salesforce integration", "Consulting partners")',
    cost_structure: '(e.g., "Cloud hosting $10K/month", "Sales team", "R&D")',
    pricing_model: '(e.g., "Freemium: free for 5 users, then $25/user/month")',
    customer_acquisition: '(e.g., "Content marketing focused on CFO pain points")',
    retention_strategy: '(e.g., "Quarterly business reviews with CSM")',

    product: '(e.g., "Intellispot Platform", "Analytics Dashboard")',
    theme: '(e.g., "AI-powered automation", "Mobile-first experience")',
    epic: '(e.g., "Automated reporting module", "User management system")',
    pattern: '(e.g., "Event-driven architecture", "Microservices pattern")',
    feature_request: '(e.g., "Customer requested bulk import from Excel")',
    requirement: '(e.g., "Must support SSO via SAML", "10K concurrent users")',
    user_story: '(e.g., "As a manager, I want to see weekly summaries")',
    use_case: '(e.g., "Month-end close for multi-entity organizations")',
    acceptance_criteria: '(e.g., "Import completes in under 30 seconds for 10K rows")',

    constraint: '(e.g., "Must work on mobile with limited connectivity", "GDPR compliance required")',
    regulation: '(e.g., "GDPR compliance", "SOX requirements", "HIPAA mandated")',
    dependency: '(e.g., "Depends on PostgreSQL 14+ for JSONB support")',
    technical_specification: '(e.g., "API must support REST and GraphQL")',
    integration_point: '(e.g., "Integrate with Stripe for payments")',
    component: '(e.g., "Authentication service handles SSO and MFA")',
    technical_debt: '(e.g., "Legacy reporting module needs refactoring")',

    action_item: '(e.g., "Update pricing page with new annual plan")',
    milestone: '(e.g., "Beta launch with 50 pilot customers by March 15")',
    release: '(e.g., "Version 2.0 includes mobile app and API improvements")',
    sprint: '(e.g., "Sprint 12 focuses on performance optimization")',
    task: '(e.g., "Implement user authentication flow")',
    test_case: '(e.g., "Test concurrent load of 1000 users")',
    bug: '(e.g., "Export fails for datasets larger than 100K rows")',

    metric: '(e.g., "Customer acquisition cost (CAC) is $5,000")',
    success_metric: '(e.g., "Reduce churn to <5%", "NPS above 50")',
    kpi: '(e.g., "Target NPS score of 50+ by end of year")',

    customer_journey: '(e.g., "Discover → evaluate → trial → purchase → advocate")',
    journey_stage: '(e.g., "Awareness: customer realizes they have problem")',
    touchpoint: '(e.g., "Initial demo call", "Product trial signup")',
    customer_need: '(e.g., "Need for mobile access while traveling")',
    quote: '(e.g., "The Salesforce integration was the deciding factor" - CTO)',
    observation: '(e.g., "Users who try mobile in week 1 have 3x retention")',
    behavioral_pattern: '(e.g., "Power users export reports on Friday afternoons")',

    weakness: '(e.g., "Limited sales team", "Technical debt in legacy code")',
    strength: '(e.g., "Strong brand recognition", "Patented technology")',
    threat: '(e.g., "New competitors entering market", "Economic downturn")',
    business_context: '(e.g., "Given the shift to remote work, collaboration features are critical")',
  }

  return examples[facetId] || ''
}

/**
 * Get default enabled facets (all with isEnabled: true)
 */
export function getDefaultEnabledFacets(): FacetTypeDefinition[] {
  return EXPANDED_FACET_SCHEMA.filter(f => f.isEnabled)
}

/**
 * Get specific facets by IDs
 */
export function getFacetsByIds(ids: string[]): FacetTypeDefinition[] {
  const idSet = new Set(ids)
  return EXPANDED_FACET_SCHEMA.filter(f => idSet.has(f.id))
}
